import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
    FaSync, FaFilter, FaCheck, FaTimes, FaUserAlt,
    FaClock, FaChevronLeft, FaChevronRight, FaSearch,
    FaCheckCircle, FaTimesCircle, FaHourglassHalf,
    FaShieldAlt
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import axiosInstance from '../utils/axiosInstance';

dayjs.extend(relativeTime);

/* ── Yardımcı: durum renk/ikon/label ────────────────────────────── */
const STATUS = {
    pending:  { label: 'Bekliyor',   color: '#3b82f6', bg: 'rgba(59,130,246,.1)',  border: 'rgba(59,130,246,.2)',  icon: <FaHourglassHalf /> },
    approved: { label: 'Onaylandı',  color: '#10b981', bg: 'rgba(16,185,129,.1)',  border: 'rgba(16,185,129,.2)',  icon: <FaCheckCircle />  },
    rejected: { label: 'Reddedildi', color: '#ef4444', bg: 'rgba(239,68,68,.1)',   border: 'rgba(239,68,68,.2)',   icon: <FaTimesCircle />  },
};

const DataTable = ({
    title,
    data = [],
    isPendingPage   = false,
    isCompletedPage = false,
    fetchData,
    handleApprove   = () => {},
    openRejectModal = () => {},
}) => {
    const [filterVisible, setFilterVisible] = useState(false);
    const [searchTerm,    setSearchTerm]    = useState('');
    const [currentPage,   setCurrentPage]   = useState(1);
    const [isSystemActive,  setIsSystemActive]  = useState(true);
    const [loadingStatus,   setLoadingStatus]   = useState(true);
    const [bonusNames,      setBonusNames]      = useState({});
    const itemsPerPage = 10;

    /* sistem durumu */
    const fetchSystemStatus = () => {
        axiosInstance.get('/siteSettings')
            .then(r => setIsSystemActive(r.data.bonusRequestsEnabled))
            .catch(console.error)
            .finally(() => setLoadingStatus(false));
    };

    useEffect(() => {
        fetchSystemStatus();
        const iv = setInterval(fetchSystemStatus, 20000);
        return () => clearInterval(iv);
    }, []);

    /* bonus isimleri */
    useEffect(() => {
        axiosInstance.get('/bonus/selection-data').then(r => {
            const m = {};
            r.data.forEach(cat => cat.items.forEach(b => { m[b.type] = b.name; }));
            setBonusNames(m);
        }).catch(console.error);
    }, []);

    const copyToClipboard = (text) => {
        if (!text || text === '—') return;
        navigator.clipboard.writeText(text)
            .then(() => toast.success('Kopyalandı!', { autoClose: 1200, hideProgressBar: true }))
            .catch(() => toast.error('Kopyalama başarısız!'));
    };

    /* filtreleme & sayfalama */
    const filteredData = data.filter(item =>
        item.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.bonusType?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    const totalPages      = Math.ceil(filteredData.length / itemsPerPage);
    const indexOfLast     = currentPage * itemsPerPage;
    const indexOfFirst    = indexOfLast - itemsPerPage;
    const currentItems    = filteredData.slice(indexOfFirst, indexOfLast);

    const paginate = (p) => { setCurrentPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); };

    const renderPagination = () => {
        const pages = [];
        let start = Math.max(1, currentPage - 2);
        let end   = Math.min(totalPages, start + 4);
        if (end === totalPages) start = Math.max(1, totalPages - 4);
        for (let i = Math.max(1, start); i <= end; i++) {
            pages.push(
                <button key={i} onClick={() => paginate(i)} style={{
                    width: 32, height: 32, borderRadius: 8, border: 'none', cursor: 'pointer',
                    fontSize: '.78rem', fontWeight: currentPage === i ? 700 : 500,
                    background: currentPage === i ? '#f27127' : 'rgba(255,255,255,.05)',
                    color: currentPage === i ? '#fff' : 'rgba(255,255,255,.4)',
                    transition: 'all .15s',
                }}>{i}</button>
            );
        }
        return pages;
    };

    /* sayfa tipine göre başlık rengi */
    const pageAccent = isPendingPage ? '#3b82f6' : isCompletedPage ? '#10b981' : '#ef4444';
    const pageIcon   = isPendingPage ? <FaHourglassHalf /> : isCompletedPage ? <FaCheckCircle /> : <FaTimesCircle />;

    return (
        <div style={{
            minHeight: '100vh', background: '#090a0c',
            padding: '28px 28px 40px', color: '#e2e8f0',
            fontFamily: "'Outfit','Inter',sans-serif", boxSizing: 'border-box',
        }}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&display=swap');
                *, *::before, *::after { box-sizing: border-box; }

                .dt-row { transition: background .15s, transform .15s !important; }
                .dt-row:hover { background: rgba(255,255,255,.035) !important; transform: translateX(2px); }

                .dt-btn-approve { transition: all .15s; }
                .dt-btn-approve:hover { background: rgba(16,185,129,.25) !important; box-shadow: 0 0 12px rgba(16,185,129,.3) !important; transform: scale(1.08); }

                .dt-btn-reject { transition: all .15s; }
                .dt-btn-reject:hover { background: rgba(239,68,68,.25) !important; box-shadow: 0 0 12px rgba(239,68,68,.3) !important; transform: scale(1.08); }

                .dt-refresh:hover { background: rgba(242,113,39,.2) !important; color: #f27127 !important; }
                .dt-filter-btn:hover { background: rgba(255,255,255,.07) !important; }

                .dt-search input:focus {
                    border-color: #f27127 !important;
                    box-shadow: 0 0 0 3px rgba(242,113,39,.1) !important;
                    outline: none;
                }

                .dt-page-nav:not(:disabled):hover { border-color: #f27127 !important; color: #f27127 !important; }
                .dt-page-num:not(.active):hover { background: rgba(255,255,255,.08) !important; color: #fff !important; }

                .dt-reason:hover {
                    border-color: rgba(242,113,39,.4) !important;
                    background: rgba(242,113,39,.07) !important;
                    color: #fff !important;
                    cursor: pointer;
                }

                @keyframes fadeUp {
                    from { opacity:0; transform:translateY(10px); }
                    to   { opacity:1; transform:none; }
                }
                .dt-fade { animation: fadeUp .3s ease both; }

                @keyframes pulseDot {
                    0%,100%{ opacity:1; transform:scale(1); }
                    50%    { opacity:.4; transform:scale(.7); }
                }
            `}</style>

            {/* ── Header ─────────────────────────────────────── */}
            <div className="dt-fade" style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                marginBottom: 24,
            }}>
                {/* Sol: başlık */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div style={{
                        width: 44, height: 44, borderRadius: 13, flexShrink: 0,
                        background: `${pageAccent}18`,
                        border: `1px solid ${pageAccent}35`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: pageAccent, fontSize: '1.1rem',
                        boxShadow: `0 0 18px ${pageAccent}20`,
                    }}>{pageIcon}</div>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <h2 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700, color: '#f8fafc', letterSpacing: '-.3px' }}>{title}</h2>
                            {/* Sayaç badge */}
                            <span style={{
                                padding: '2px 10px', borderRadius: 20,
                                background: `${pageAccent}18`, color: pageAccent,
                                border: `1px solid ${pageAccent}30`,
                                fontSize: '.7rem', fontWeight: 700,
                            }}>{filteredData.length}</span>
                        </div>
                        <p style={{ margin: '3px 0 0', fontSize: '.71rem', color: 'rgba(255,255,255,.22)' }}>
                            Yönetim Paneli · Canlı Kontrol
                        </p>
                    </div>
                </div>

                {/* Sağ: aksiyonlar */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {/* Sistem durumu */}
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 7,
                        padding: '7px 14px', borderRadius: 40,
                        background: isSystemActive ? 'rgba(16,185,129,.08)' : 'rgba(239,68,68,.08)',
                        border: `1px solid ${isSystemActive ? 'rgba(16,185,129,.2)' : 'rgba(239,68,68,.2)'}`,
                        fontSize: '.73rem', fontWeight: 600,
                        color: isSystemActive ? '#10b981' : '#ef4444',
                    }}>
                        <span style={{
                            width: 6, height: 6, borderRadius: '50%', display: 'inline-block',
                            background: isSystemActive ? '#10b981' : '#ef4444',
                            boxShadow: `0 0 7px ${isSystemActive ? '#10b981' : '#ef4444'}`,
                            animation: 'pulseDot 2s infinite',
                        }} />
                        {loadingStatus ? '...' : isSystemActive ? 'Sistem Aktif' : 'Sistem Kapalı'}
                    </div>

                    {/* Filtre */}
                    <button className="dt-filter-btn" onClick={() => setFilterVisible(v => !v)} style={{
                        display: 'flex', alignItems: 'center', gap: 7,
                        padding: '8px 14px', borderRadius: 10, border: 'none', cursor: 'pointer',
                        background: filterVisible ? 'rgba(242,113,39,.12)' : 'rgba(255,255,255,.05)',
                        color: filterVisible ? '#f27127' : 'rgba(255,255,255,.5)',
                        fontSize: '.78rem', fontWeight: 600, transition: 'all .15s',
                        fontFamily: 'inherit',
                    }}>
                        <FaFilter style={{ fontSize: '.7rem' }} />
                        {filterVisible ? 'Kapat' : 'Filtrele'}
                    </button>

                    {/* Yenile */}
                    {!isPendingPage && (
                        <button className="dt-refresh" onClick={fetchData} style={{
                            display: 'flex', alignItems: 'center', gap: 7,
                            padding: '8px 14px', borderRadius: 10, border: 'none', cursor: 'pointer',
                            background: 'rgba(255,255,255,.05)',
                            color: 'rgba(255,255,255,.5)',
                            fontSize: '.78rem', fontWeight: 600, transition: 'all .15s',
                            fontFamily: 'inherit',
                        }}>
                            <FaSync style={{ fontSize: '.7rem' }} />
                            Yenile
                        </button>
                    )}
                </div>
            </div>

            {/* ── Arama ──────────────────────────────────────── */}
            {filterVisible && (
                <div className="dt-search dt-fade" style={{ position: 'relative', marginBottom: 18 }}>
                    <FaSearch style={{
                        position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)',
                        color: 'rgba(255,255,255,.25)', fontSize: '.85rem', pointerEvents: 'none',
                    }} />
                    <input
                        type="text"
                        placeholder="Kullanıcı veya bonus türü ara..."
                        value={searchTerm}
                        onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                        style={{
                            width: '100%', padding: '11px 14px 11px 40px',
                            background: '#111318', border: '1px solid rgba(255,255,255,.08)',
                            borderRadius: 12, color: '#f1f5f9', fontSize: '.84rem',
                            fontFamily: 'inherit', transition: 'all .2s',
                        }}
                    />
                </div>
            )}

            {/* ── Tablo kartı ────────────────────────────────── */}
            <div className="dt-fade" style={{
                background: '#111318',
                border: '1px solid rgba(255,255,255,.06)',
                borderRadius: 20, overflow: 'hidden',
            }}>
                {/* Tablo başlık çubuğu */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: isPendingPage
                        ? '140px 1fr 110px 1fr 130px 90px 110px 100px'
                        : '140px 1fr 110px 1fr 130px 110px',
                    padding: '12px 18px',
                    borderBottom: '1px solid rgba(255,255,255,.05)',
                    background: 'rgba(0,0,0,.2)',
                }}>
                    {['KULLANICI', 'BONUS TÜRÜ', 'DURUM',
                      isCompletedPage || isPendingPage ? 'BİLGİ / NOT' : 'RED NEDENİ',
                      'TARİH',
                      ...(isPendingPage ? ['BEKLEME'] : []),
                      'ATANAN',
                      ...(isPendingPage ? ['İŞLEMLER'] : []),
                    ].map((h, i) => (
                        <div key={i} style={{
                            fontSize: '.6rem', fontWeight: 700,
                            textTransform: 'uppercase', letterSpacing: '1.2px',
                            color: 'rgba(255,255,255,.2)',
                            textAlign: h === 'İŞLEMLER' ? 'center' : 'left',
                        }}>{h}</div>
                    ))}
                </div>

                {/* Satırlar */}
                <div style={{ padding: '6px 8px' }}>
                    {currentItems.length > 0 ? currentItems.map((item, idx) => {
                        const st     = STATUS[item.status] || STATUS.pending;
                        const bonus  = bonusNames[item.bonusType];
                        const note   = item.rejectReason || item.adminNote;

                        return (
                            <div key={item._id} className="dt-row" style={{
                                display: 'grid',
                                gridTemplateColumns: isPendingPage
                                    ? '140px 1fr 110px 1fr 130px 90px 110px 100px'
                                    : '140px 1fr 110px 1fr 130px 110px',
                                alignItems: 'center',
                                padding: '11px 10px',
                                borderRadius: 12, marginBottom: 4,
                                background: 'rgba(255,255,255,.015)',
                                border: '1px solid rgba(255,255,255,.03)',
                                position: 'relative', overflow: 'hidden',
                            }}>
                                {/* Sol accent çizgi */}
                                <div style={{
                                    position: 'absolute', left: 0, top: '18%', bottom: '18%',
                                    width: 3, borderRadius: '0 3px 3px 0',
                                    background: st.color, boxShadow: `0 0 8px ${st.color}70`,
                                }} />

                                {/* Kullanıcı — tıklayınca kopyala */}
                                <div style={{ paddingLeft: 8 }}>
                                    <span
                                        onClick={() => copyToClipboard(item.username)}
                                        title="Kopyalamak için tıkla"
                                        style={{
                                            fontSize: '.82rem', fontWeight: 700, color: '#f1f5f9',
                                            cursor: 'pointer', borderBottom: '1px dashed rgba(255,255,255,.15)',
                                            transition: 'color .15s, border-color .15s',
                                        }}
                                        onMouseEnter={e => { e.currentTarget.style.color = '#f27127'; e.currentTarget.style.borderColor = 'rgba(242,113,39,.4)'; }}
                                        onMouseLeave={e => { e.currentTarget.style.color = '#f1f5f9'; e.currentTarget.style.borderColor = 'rgba(255,255,255,.15)'; }}
                                    >{item.username}</span>
                                </div>

                                {/* Bonus */}
                                <div style={{ minWidth: 0, paddingRight: 10 }}>
                                    {bonus
                                        ? <span style={{ fontSize: '.8rem', fontWeight: 600, color: '#e2e8f0', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{bonus}</span>
                                        : <span style={{ fontSize: '.75rem', color: 'rgba(255,255,255,.2)' }}>{item.bonusType}</span>
                                    }
                                </div>

                                {/* Durum */}
                                <div>
                                    <span style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 5,
                                        padding: '3px 10px', borderRadius: 7,
                                        fontSize: '.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.4px',
                                        background: st.bg, color: st.color, border: `1px solid ${st.border}`,
                                    }}>
                                        <span style={{ fontSize: '.6rem' }}>{st.icon}</span>
                                        {st.label}
                                    </span>
                                </div>

                                {/* Not / Red sebebi */}
                                <div style={{ paddingRight: 12 }}>
                                    {note ? (
                                        <div className="dt-reason" onClick={() => copyToClipboard(note)} title={note} style={{
                                            padding: '4px 10px', borderRadius: 7,
                                            background: 'rgba(0,0,0,.3)', border: '1px solid rgba(255,255,255,.06)',
                                            fontSize: '.7rem', color: '#94a3b8',
                                            cursor: 'pointer', transition: 'all .15s',
                                            display: 'flex', alignItems: 'center', gap: 6,
                                            maxWidth: '100%',
                                        }}>
                                            <FaSearch style={{ fontSize: '.55rem', flexShrink: 0, opacity: .5 }} />
                                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {note.length > 42 ? note.slice(0, 42) + '…' : note}
                                            </span>
                                        </div>
                                    ) : (
                                        <span style={{ color: 'rgba(255,255,255,.12)', fontSize: '.75rem' }}>—</span>
                                    )}
                                </div>

                                {/* Tarih */}
                                <div>
                                    <div style={{ fontSize: '.78rem', fontWeight: 500, color: '#cbd5e1' }}>
                                        {dayjs(item.createdAt).format('DD MMM HH:mm')}
                                    </div>
                                    <div style={{ fontSize: '.65rem', color: 'rgba(255,255,255,.2)', marginTop: 2 }}>
                                        {dayjs(item.createdAt).format('YYYY')}
                                    </div>
                                </div>

                                {/* Bekleme (sadece pending) */}
                                {isPendingPage && (
                                    <div>
                                        <span style={{
                                            display: 'inline-flex', alignItems: 'center', gap: 5,
                                            fontSize: '.72rem', fontWeight: 600, color: '#f59e0b',
                                        }}>
                                            <FaClock style={{ fontSize: '.65rem' }} />
                                            {dayjs(item.createdAt).fromNow(true)}
                                        </span>
                                    </div>
                                )}

                                {/* Atanan */}
                                <div>
                                    {item.assignedTo ? (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <div style={{
                                                width: 22, height: 22, borderRadius: 6, flexShrink: 0,
                                                background: 'rgba(242,113,39,.12)', border: '1px solid rgba(242,113,39,.2)',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                color: '#f27127', fontSize: '.55rem', fontWeight: 700,
                                            }}>{item.assignedTo.slice(0,2).toUpperCase()}</div>
                                            <span style={{ fontSize: '.75rem', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.assignedTo}</span>
                                        </div>
                                    ) : (
                                        <span style={{ color: 'rgba(255,255,255,.12)', fontSize: '.75rem' }}>—</span>
                                    )}
                                </div>

                                {/* İşlemler (sadece pending) */}
                                {isPendingPage && (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                                        <button className="dt-btn-approve" onClick={() => handleApprove(item.username)} title="Onayla" style={{
                                            width: 32, height: 32, borderRadius: 9, border: 'none', cursor: 'pointer',
                                            background: 'rgba(16,185,129,.15)', color: '#10b981',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.8rem',
                                        }}>
                                            <FaCheck />
                                        </button>
                                        <button className="dt-btn-reject" onClick={() => openRejectModal(item.username)} title="Reddet" style={{
                                            width: 32, height: 32, borderRadius: 9, border: 'none', cursor: 'pointer',
                                            background: 'rgba(239,68,68,.15)', color: '#ef4444',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.8rem',
                                        }}>
                                            <FaTimes />
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    }) : (
                        <div style={{
                            textAlign: 'center', padding: '56px 0',
                            color: 'rgba(255,255,255,.15)', fontSize: '.85rem',
                        }}>
                            <div style={{ fontSize: '2.5rem', marginBottom: 12, opacity: .3 }}>{pageIcon}</div>
                            Gösterilecek kayıt bulunamadı
                        </div>
                    )}
                </div>

                {/* ── Pagination ─────────────────────────────── */}
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '14px 18px',
                    borderTop: '1px solid rgba(255,255,255,.05)',
                    background: 'rgba(0,0,0,.15)',
                }}>
                    <span style={{ fontSize: '.72rem', color: 'rgba(255,255,255,.2)' }}>
                        <b style={{ color: 'rgba(255,255,255,.45)' }}>{filteredData.length}</b> kayıttan&nbsp;
                        <b style={{ color: 'rgba(255,255,255,.45)' }}>{indexOfFirst + 1}–{Math.min(indexOfLast, filteredData.length)}</b> arası
                    </span>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <button className="dt-page-nav" disabled={currentPage === 1} onClick={() => paginate(currentPage - 1)} style={{
                            width: 32, height: 32, borderRadius: 8, border: '1px solid rgba(255,255,255,.08)',
                            background: 'rgba(255,255,255,.04)', color: 'rgba(255,255,255,.3)',
                            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            transition: 'all .15s', opacity: currentPage === 1 ? .35 : 1,
                        }}><FaChevronLeft style={{ fontSize: '.65rem' }} /></button>

                        {renderPagination()}

                        <button className="dt-page-nav" disabled={currentPage === totalPages} onClick={() => paginate(currentPage + 1)} style={{
                            width: 32, height: 32, borderRadius: 8, border: '1px solid rgba(255,255,255,.08)',
                            background: 'rgba(255,255,255,.04)', color: 'rgba(255,255,255,.3)',
                            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            transition: 'all .15s', opacity: currentPage === totalPages ? .35 : 1,
                        }}><FaChevronRight style={{ fontSize: '.65rem' }} /></button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DataTable;
